import ClassIcon from '@mui/icons-material/Class';
import EventIcon from '@mui/icons-material/Event';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ShortTextIcon from '@mui/icons-material/ShortText';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { DependencyCategory, DependencyPriority, DependencyType, IDependency, IIssue, IRisk } from '@pulse/shared';
import {
  Alert,
  Button,
  Chip,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  ICON_SIZE,
  InputLabel,
  LabelWithIcon,
  Link as MuiLink,
  MarkdownField,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@pulse/ui';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AttachmentsPanel } from '../../../components/AttachmentsPanel';
import { CopyLinkButton } from '../../../components/CopyLinkButton';
import { EntityPicker } from '../../../components/EntityPicker';
import { MultiSelectPicker } from '../../../components/MultiSelectPicker';
import { StagedAttachmentsPanel, useStagedAttachments } from '../../../components/StagedAttachmentsPanel';
import { useUserLookup } from '../../access-management/hooks/useUsers';
import { dependencyAttachmentDownloadUrl, uploadDependencyAttachment } from '../api/dependency.api';
import {
  useCreateDependency,
  useDependencyAttachments,
  useRemoveDependencyAttachment,
  useTransitionDependency,
  useUpdateDependency,
  useUploadDependencyAttachment,
} from '../hooks/useDependencies';
import { useIssues } from '../hooks/useIssues';
import { useRisks } from '../hooks/useRisks';
import { useWorkflowDefinition } from '../hooks/useWorkflow';
import { dependencyLink } from '../utils/entityLinks';

const SHOW_CATEGORY_FIELD = import.meta.env.VITE_SHOW_DEPENDENCY_CATEGORY_FIELD !== 'false';
const TYPES: DependencyType[] = ['Internal', 'Customer', 'External'];
const CATEGORIES: DependencyCategory[] = ['Finish-to-Start', 'Start-to-Start', 'Finish-to-Finish', 'Start-to-Finish'];
const PRIORITIES: DependencyPriority[] = ['Critical', 'High', 'Medium', 'Low'];
// Matches CreateDependencyDto's @MaxLength decorators (apps/api/src/modules/presales/dto/create-dependency.dto.ts).
const TITLE_MAX = 255;
const TEXT_MAX = 1000;

function overLimitHelperText(value: string, max: number, overLimit: boolean): string {
  return overLimit ? `${value.length}/${max} — ${value.length - max} characters over the limit` : `${value.length}/${max}`;
}

// Stable (module-level) empty-array fallbacks — an inline `= []` default is a fresh reference
// on every render while the query is loading, which would make the resync useEffect below
// (which depends on `issues`/`risks`) fire on every render and loop.
const EMPTY_ISSUES: IIssue[] = [];
const EMPTY_RISKS: IRisk[] = [];

interface DependencyFormDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  dependency: IDependency | null;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

export function DependencyFormDialog({ projectId, open, onClose, dependency, onSaved, onError }: DependencyFormDialogProps) {
  const isEdit = Boolean(dependency);
  const { data: users = [] } = useUserLookup();
  const { data: issues = EMPTY_ISSUES } = useIssues(projectId);
  const { data: risks = EMPTY_RISKS } = useRisks(projectId);
  const { data: workflowDefinition } = useWorkflowDefinition('dependency');
  const statusOptions = workflowDefinition?.steps.map((s) => s.name) ?? [];

  const [title, setTitle] = useState(dependency?.title ?? '');
  const [description, setDescription] = useState(dependency?.description ?? '');
  const [impact, setImpact] = useState(dependency?.impact ?? '');
  const [type, setType] = useState<DependencyType | ''>(dependency?.type ?? '');
  const [category, setCategory] = useState<DependencyCategory | ''>(dependency?.category ?? '');
  const [ownerId, setOwnerId] = useState(dependency?.ownerId ?? '');
  const [dueDate, setDueDate] = useState(dependency?.dueDate ?? '');
  const [priority, setPriority] = useState<DependencyPriority | ''>(dependency?.priority ?? '');
  // Staged locally like every other field — the Select shows this, not dependency.status
  // directly, so picking a new status doesn't touch the backend until Save (see handleSave).
  const [pendingStatus, setPendingStatus] = useState(dependency?.status ?? '');
  const [linkedIssues, setLinkedIssues] = useState<IIssue[]>(issues.filter((i) => dependency?.linkedIssueIds.includes(i.id)));
  const [linkedRisks, setLinkedRisks] = useState<IRisk[]>(risks.filter((r) => dependency?.linkedRiskIds.includes(r.id)));
  const [formError, setFormError] = useState<string | null>(null);
  const [dueDateFieldValid, setDueDateFieldValid] = useState(true);
  const {
    staged,
    add: addStaged,
    remove: removeStaged,
    reset: resetStaged,
    uploadAll,
    isUploading: isUploadingStaged,
  } = useStagedAttachments();

  // The dialog is mounted once and toggled via `open` rather than remounted per edit, so the
  // `useState(dependency?.x ?? '')` initializers above only ever run for whichever dependency
  // was current on first mount — this re-syncs the form to the dependency actually being
  // edited (or clears it back to blank for a new one) whenever the target, open-state, or the
  // linkable-issue/risk lists change (same pattern as SprintFormDialog.tsx).
  useEffect(() => {
    setTitle(dependency?.title ?? '');
    setDescription(dependency?.description ?? '');
    setImpact(dependency?.impact ?? '');
    setType(dependency?.type ?? '');
    setCategory(dependency?.category ?? '');
    setOwnerId(dependency?.ownerId ?? '');
    setDueDate(dependency?.dueDate ?? '');
    setPriority(dependency?.priority ?? '');
    setPendingStatus(dependency?.status ?? '');
    setLinkedIssues(issues.filter((i) => dependency?.linkedIssueIds.includes(i.id)));
    setLinkedRisks(risks.filter((r) => dependency?.linkedRiskIds.includes(r.id)));
    setFormError(null);
    setDueDateFieldValid(true);
    resetStaged();
  }, [dependency, open, issues, risks, resetStaged]);

  const createMutation = useCreateDependency(projectId);
  const updateMutation = useUpdateDependency(projectId);
  const transitionMutation = useTransitionDependency(projectId);
  const { data: attachments = [], isLoading: attachmentsLoading } = useDependencyAttachments(projectId, dependency?.id ?? '');
  const uploadMutation = useUploadDependencyAttachment(projectId, dependency?.id ?? '');
  const removeAttachmentMutation = useRemoveDependencyAttachment(projectId, dependency?.id ?? '');

  const titleOverLimit = title.length > TITLE_MAX;
  const descriptionOverLimit = description.length > TEXT_MAX;
  const impactOverLimit = impact.length > TEXT_MAX;
  const isFormValid =
    title.trim().length > 0 &&
    ownerId !== '' &&
    !titleOverLimit &&
    !descriptionOverLimit &&
    !impactOverLimit &&
    dueDateFieldValid;

  function handleSave() {
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      impact: impact.trim() || null,
      type: type || null,
      category: category || null,
      ownerId,
      dueDate: dueDate || null,
      priority: priority || null,
      linkedIssueIds: linkedIssues.map((i) => i.id),
      linkedRiskIds: linkedRisks.map((r) => r.id),
    };
    if (isEdit && dependency) {
      const statusChanged = pendingStatus !== dependency.status;
      if (!statusChanged) {
        updateMutation.mutate(
          { dependencyId: dependency.id, data: payload },
          {
            onSuccess: () => { setFormError(null); onSaved('Dependency updated successfully.'); onClose(); },
            onError: (err: Error) => setFormError(err.message),
          },
        );
        return;
      }

      // Status changed (possibly along with other fields) — the workflow transition and the
      // field update are separate, non-transactional backend endpoints, so they're combined into
      // one Save action here rather than fired independently on every field change. Transition
      // fires first: it's the one independently validated against the dependency's
      // WorkflowDefinition, so a rejection there leaves nothing written at all instead of a
      // partially-saved record.
      const original = {
        title: dependency.title,
        description: dependency.description || undefined,
        impact: dependency.impact || undefined,
        type: dependency.type || undefined,
        category: dependency.category || undefined,
        ownerId: dependency.ownerId,
        dueDate: dependency.dueDate || undefined,
        priority: dependency.priority || undefined,
        linkedIssueIds: [...dependency.linkedIssueIds].sort(),
        linkedRiskIds: [...dependency.linkedRiskIds].sort(),
      };
      const current = {
        ...payload,
        linkedIssueIds: [...payload.linkedIssueIds].sort(),
        linkedRiskIds: [...payload.linkedRiskIds].sort(),
      };
      const fieldsChanged = JSON.stringify(current) !== JSON.stringify(original);

      void (async () => {
        try {
          await transitionMutation.mutateAsync({ dependencyId: dependency.id, toStep: pendingStatus });

          if (fieldsChanged) {
            try {
              await updateMutation.mutateAsync({ dependencyId: dependency.id, data: payload });
            } catch (err) {
              // Transition already committed (and its own cache invalidation already made the
              // table consistent) — report exactly what happened rather than a vague combined
              // message. Re-opening Edit is the correct way to retry just the field changes.
              setFormError(
                `Status changed to "${pendingStatus}" successfully, but the other changes could not be saved: ${err instanceof Error ? err.message : 'unknown error'}.`,
              );
              onClose();
              return;
            }
          }

          setFormError(null);
          onSaved(fieldsChanged ? 'Dependency updated successfully.' : `Dependency moved to "${pendingStatus}".`);
          onClose();
        } catch (err) {
          setFormError(err instanceof Error ? err.message : 'Failed to change status.');
        }
      })();
      return;
    }
    const filesToUpload = staged;
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        void (async () => {
          const uploadOk =
            filesToUpload.length === 0 ||
            (await uploadAll(filesToUpload, (file) => uploadDependencyAttachment(projectId, created.id, file)));
          resetStaged();
          setFormError(null);
          onSaved(
            uploadOk
              ? 'Dependency created successfully.'
              : 'Dependency created successfully, but some attachments failed to upload.',
          );
          onClose();
        })();
      },
      onError: (err: Error) => setFormError(err.message),
    });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending || transitionMutation.isPending || isUploadingStaged;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ wordBreak: 'break-word' }}>
        {isEdit && dependency ? (
          <MuiLink
            component={RouterLink}
            to={dependencyLink(projectId, dependency.dependencyKey)}
            onClick={onClose}
            sx={{ wordBreak: 'break-word' }}
          >
            {dependency.dependencyKey} — {dependency.title}
          </MuiLink>
        ) : (
          <span>Add a Dependency</span>
        )}
        {isEdit && dependency && (
          <span style={{ marginLeft: 6, verticalAlign: 'middle' }}>
            <CopyLinkButton path={dependencyLink(projectId, dependency.dependencyKey)} />
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          {isEdit && dependency ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel><LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
                <Select
                  value={pendingStatus}
                  label={<LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline />}
                  onChange={(e) => setPendingStatus(e.target.value)}
                  disabled={isSaving}
                >
                  {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {!dependency.isActive && <Chip size="small" label="Inactive" color="default" />}
            </Stack>
          ) : (
            <TextField
              label={<LabelWithIcon label="Status" icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} inline />}
              value={workflowDefinition?.steps.find((s) => s.isInitial)?.name ?? statusOptions[0] ?? ''}
              disabled
              size="small"
              sx={{ maxWidth: 220 }}
              helperText="New dependencies start in this status"
            />
          )}

          <TextField
            label={<LabelWithIcon label="Title" icon={<ShortTextIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTitle((t) => t.trim())}
            required
            fullWidth
            error={titleOverLimit}
            helperText={overLimitHelperText(title, TITLE_MAX, titleOverLimit)}
          />
          <MarkdownField
            label={<LabelWithIcon label="Description" icon={<NotesIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={description}
            onChange={setDescription}
            error={descriptionOverLimit}
            helperText={overLimitHelperText(description, TEXT_MAX, descriptionOverLimit)}
          />
          <TextField
            label={<LabelWithIcon label="Impact" icon={<PriorityHighIcon fontSize={ICON_SIZE.sm} />} inline />}
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
            fullWidth
            multiline
            rows={2}
            error={impactOverLimit}
            helperText={overLimitHelperText(impact, TEXT_MAX, impactOverLimit)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel><LabelWithIcon label="Type" icon={<ClassIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
              <Select
                value={type}
                label={<LabelWithIcon label="Type" icon={<ClassIcon fontSize={ICON_SIZE.sm} />} inline />}
                onChange={(e) => setType(e.target.value as DependencyType)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            {SHOW_CATEGORY_FIELD && (
              <FormControl fullWidth>
                <InputLabel><LabelWithIcon label="Category" icon={<LayersIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
                <Select
                  value={category}
                  label={<LabelWithIcon label="Category" icon={<LayersIcon fontSize={ICON_SIZE.sm} />} inline />}
                  onChange={(e) => setCategory(e.target.value as DependencyCategory)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel><LabelWithIcon label="Priority" icon={<WarningAmberIcon fontSize={ICON_SIZE.sm} />} inline /></InputLabel>
              <Select
                value={priority}
                label={<LabelWithIcon label="Priority" icon={<WarningAmberIcon fontSize={ICON_SIZE.sm} />} inline />}
                onChange={(e) => setPriority(e.target.value as DependencyPriority)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <EntityPicker
              label="Owner"
              labelIcon={<PersonIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative'}} />}
              required
              options={users}
              value={users.find((u) => u.id === ownerId) ?? null}
              onChange={(u) => setOwnerId(u?.id ?? '')}
            />
            <DateField
              label={<LabelWithIcon label="Due Date" icon={<EventIcon fontSize={ICON_SIZE.sm} />} inline />}
              value={dueDate}
              onChange={setDueDate}
              onValidityChange={setDueDateFieldValid}
              invalidMessage="Please enter a valid Due Date."
              fullWidth
            />
          </Stack>

          <MultiSelectPicker<IIssue>
            label="Link Issues"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={issues}
            value={linkedIssues}
            onChange={setLinkedIssues}
            getOptionKey={(i) => i.id}
            getOptionLabel={(i) => `${i.issueKey} — ${i.name}`}
          />
          <MultiSelectPicker<IRisk>
            label="Link Risks"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={risks}
            value={linkedRisks}
            onChange={setLinkedRisks}
            getOptionKey={(r) => r.id}
            getOptionLabel={(r) => `${r.riskKey} — ${r.title}`}
          />

          <Divider />
          {isEdit && dependency ? (
            <AttachmentsPanel
              attachments={attachments}
              isLoading={attachmentsLoading}
              onUpload={(file) => uploadMutation.mutate(file, { onError: (err: Error) => onError(err.message) })}
              isUploading={uploadMutation.isPending}
              onRemove={(id) =>
                removeAttachmentMutation.mutate(id, {
                  onSuccess: () => onSaved('Attachment deleted successfully.'),
                  onError: (err: Error) => onError(err.message),
                })
              }
              isRemoving={removeAttachmentMutation.isPending}
              downloadUrl={(id) => dependencyAttachmentDownloadUrl(projectId, dependency.id, id)}
            />
          ) : (
            <StagedAttachmentsPanel staged={staged} onAdd={addStaged} onRemove={removeStaged} isUploading={isUploadingStaged} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isFormValid || isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
